/** Which dashboard cards are hidden. Missing ids stay visible, so a new card shows up on its own. */

const STORAGE_KEY = 'nextcompli.dashboard-widgets.v1';

export type DashboardWidgetChoice = {
  id: string;
  en: string;
  es: string;
};

export const HOME_DASHBOARD_WIDGETS: DashboardWidgetChoice[] = [
  { id: 'status', en: 'Saved steps', es: 'Pasos guardados' },
  { id: 'priority', en: 'Priority mix', es: 'Mezcla de prioridad' },
  { id: 'dates', en: 'Upcoming dates', es: 'Fechas próximas' },
  { id: 'accounts', en: 'By account and project', es: 'Por cuenta y proyecto' },
  { id: 'areas', en: 'By compliance area', es: 'Por área de cumplimiento' },
  { id: 'waiting', en: 'Waiting on others', es: 'En espera de otros' },
  { id: 'ready', en: 'Ready to do', es: 'Listo para hacer' },
];

export const COMPLIANCE_DASHBOARD_WIDGETS: DashboardWidgetChoice[] = [
  { id: 'finished', en: 'Finished share', es: 'Parte terminada' },
  { id: 'status', en: 'Saved steps', es: 'Pasos guardados' },
  { id: 'where', en: 'Where the work sits', es: 'Dónde está el trabajo' },
  { id: 'priority', en: 'Priority mix', es: 'Mezcla de prioridad' },
  { id: 'dates', en: 'Upcoming dates', es: 'Fechas próximas' },
  { id: 'groups', en: 'By group', es: 'Por grupo' },
  { id: 'due', en: 'Tasks by due date', es: 'Tareas por fecha' },
  { id: 'ready', en: 'Ready to do', es: 'Listo para hacer' },
  { id: 'group-chart', en: 'Tasks by group', es: 'Tareas por grupo' },
  { id: 'activity', en: 'Recent activity', es: 'Actividad reciente' },
  { id: 'months', en: 'Dates by month', es: 'Fechas por mes' },
];

export const readHiddenWidgets = (surface: string): Set<string> => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) as Record<string, unknown> : {};
    const ids = parsed[surface];
    if (!Array.isArray(ids)) {
      return new Set();
    }
    return new Set(ids.filter((id): id is string => typeof id === 'string'));
  } catch {
    return new Set();
  }
};

export type DashboardLayout = {
  order: string[];
  hidden: string[];
};

const LAYOUT_KEY = 'nextcompli.dashboard-layout.v1';

/** Keep every known card, drop unknown ids, and put new cards at the end. */
export const normalizeLayout = (
  widgets: readonly DashboardWidgetChoice[],
  raw: unknown,
): DashboardLayout => {
  const ids = widgets.map((widget) => widget.id);
  const body = raw && typeof raw === 'object' ? raw as { order?: unknown; hidden?: unknown } : {};
  const seen = new Set<string>();
  const order: string[] = [];
  const push = (id: string) => {
    if (!ids.includes(id) || seen.has(id)) {
      return;
    }
    seen.add(id);
    order.push(id);
  };
  if (Array.isArray(body.order)) {
    body.order.forEach((id) => {
      if (typeof id === 'string') {
        push(id);
      }
    });
  }
  ids.forEach(push);
  const hidden = Array.isArray(body.hidden)
    ? body.hidden.filter((id): id is string => typeof id === 'string' && ids.includes(id))
    : [];
  return { order, hidden };
};

export const readLayout = (surface: string, widgets: readonly DashboardWidgetChoice[]): DashboardLayout => {
  try {
    const raw = window.localStorage.getItem(LAYOUT_KEY);
    const parsed = raw ? JSON.parse(raw) as Record<string, unknown> : {};
    if (parsed[surface]) {
      return normalizeLayout(widgets, parsed[surface]);
    }
  } catch {
    /* fall through to the older hide list */
  }
  return normalizeLayout(widgets, { hidden: [...readHiddenWidgets(surface)] });
};

export const writeLayout = (surface: string, layout: DashboardLayout): void => {
  let parsed: Record<string, DashboardLayout> = {};
  try {
    const raw = window.localStorage.getItem(LAYOUT_KEY);
    const body = raw ? JSON.parse(raw) as Record<string, unknown> : {};
    Object.entries(body).forEach(([key, value]) => {
      if (value && typeof value === 'object') {
        parsed[key] = value as DashboardLayout;
      }
    });
  } catch {
    parsed = {};
  }
  parsed[surface] = layout;
  window.localStorage.setItem(LAYOUT_KEY, JSON.stringify(parsed));
  writeHiddenWidgets(surface, new Set(layout.hidden));
};

export type AccountPreferenceResult = { ok: boolean; value: unknown };

export const readAccountPreference = async (
  gatewayBaseUrl: string,
  accessToken: string,
  key: string,
): Promise<AccountPreferenceResult> => {
  const base = gatewayBaseUrl.replace(/\/$/, '');
  if (!base || !accessToken) {
    return { ok: false, value: null };
  }
  try {
    const response = await fetch(`${base}/api/v1/account/preferences?key=${encodeURIComponent(key)}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!response.ok) {
      return { ok: false, value: null };
    }
    const body = await response.json() as { value?: unknown };
    return { ok: true, value: body?.value ?? null };
  } catch {
    return { ok: false, value: null };
  }
};

export const writeAccountPreference = async (
  gatewayBaseUrl: string,
  accessToken: string,
  key: string,
  value: unknown,
): Promise<void> => {
  const base = gatewayBaseUrl.replace(/\/$/, '');
  if (!base || !accessToken) {
    return;
  }
  try {
    await fetch(`${base}/api/v1/account/preferences`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ key, value }),
    });
  } catch {
    /* the browser copy remains until the next save */
  }
};

export const writeHiddenWidgets = (surface: string, hidden: ReadonlySet<string>): void => {
  let parsed: Record<string, string[]> = {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const body = raw ? JSON.parse(raw) as Record<string, unknown> : {};
    Object.entries(body).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        parsed[key] = value.filter((id): id is string => typeof id === 'string');
      }
    });
  } catch {
    parsed = {};
  }
  parsed[surface] = Array.from(hidden);
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
};
